// src/services/api/config.js
import axios from 'axios';
import { handleApiError } from '../../utils/errorHandler';

// EasyCheck API configuration
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://app.insight360apps.com/easycheck/api';

// Create axios instance with EasyCheck specific config
const easyCheckApi = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000, // Longer timeout for printing operations
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  }
});

// Request interceptor - Add auth token to requests
easyCheckApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('easyCheckToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      console.log(`🔐 Token added to request: ${token.substring(0, 20)}...`);
    } else {
      console.warn('⚠️ No token found in localStorage');
    }
    
    // Add request timestamp for debugging
    config.metadata = { startTime: new Date() };
    
    console.log(`🚀 EasyCheck API Request: ${config.method?.toUpperCase()} ${config.url}`);
    return config;
  },
  (error) => {
    console.error('❌ Request Error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor - Handle EasyCheck specific responses
easyCheckApi.interceptors.response.use(
  (response) => {
    // Calculate request duration
    const duration = new Date() - response.config.metadata.startTime;
    console.log(`✅ EasyCheck API Response: ${response.config.url} (${duration}ms)`);
    
    return response;
  },
  (error) => {
    console.error('❌ EasyCheck API Error:', error);
    
    // Handle 401 specifically for auth cleanup
    if (error.response?.status === 401) {
      // Unauthorized - clear token and redirect to login
      localStorage.removeItem('easyCheckToken');
      localStorage.removeItem('easyCheckUser');
      localStorage.removeItem('easyCheckLoginTime');
      localStorage.removeItem('easyCheckRedirectUrl');
      
      // Don't interfere with super admin or mobile routes
      const isSuperAdminRoute = window.location.pathname.startsWith('/super-admin');
      const isMobileRoute = window.location.pathname.startsWith('/mobile');

      // TEMPORARILY DISABLE LOGIN REDIRECT DURING SIGNUP FLOW
      const isSignupFlow = window.location.pathname.includes('/signup') ||
                          sessionStorage.getItem('signupData');

      if (isSuperAdminRoute) {
        console.warn('🟠 401 error on super-admin route - NOT redirecting to regular login');
      } else if (isMobileRoute) {
        console.warn('🟠 401 error on mobile route - NOT redirecting to regular login');
      } else if (isSignupFlow) {
        console.warn('🟠 401 error during signup flow - NOT redirecting to login');
        console.warn('🟠 This is expected during payment creation for new signups');
      } else {
        // Only redirect if not already on login page and not in signup flow
        if (!window.location.pathname.includes('/login')) {
          window.location.href = '/login';
        }
      }
    }
    
    return Promise.reject(error);
  }
);

export default easyCheckApi;

// API response wrapper for consistent error handling
export const easyCheckRequest = async (requestFn) => {
  try {
    const response = await requestFn();
    console.log('📋 Raw API Response:', response.data);
    
    // Handle different response structures
    const responseData = response.data;
    const success = responseData.Success || responseData.success || true;
    const data = responseData.Data || responseData.data || responseData;
    const message = responseData.Message || responseData.message || 'Success';
    const timestamp = responseData.Timestamp || responseData.timestamp;
    
    const result = {
      success,
      data,
      message,
      timestamp,
      // Preserve pagination metadata for DataGrid
      TotalCount: responseData.TotalCount,
      PageNumber: responseData.PageNumber,
      PageSize: responseData.PageSize,
      TotalPages: responseData.TotalPages,
      HasNextPage: responseData.HasNextPage,
      HasPreviousPage: responseData.HasPreviousPage,
      // Also preserve lowercase versions for compatibility
      totalCount: responseData.TotalCount || responseData.totalCount,
      pageNumber: responseData.PageNumber || responseData.pageNumber,
      pageSize: responseData.PageSize || responseData.pageSize,
      totalPages: responseData.TotalPages || responseData.totalPages,
      hasNextPage: responseData.HasNextPage || responseData.hasNextPage,
      hasPreviousPage: responseData.HasPreviousPage || responseData.hasPreviousPage
    };
    
    console.log('🔄 Processed API Response:', result);
    return result;
  } catch (error) {
    // Use the new error handler to get user-friendly messages
    return handleApiError(error);
  }
};

// Stripe Configuration
export const stripeConfig = {
  publishableKey: process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_test_...',
  webhookSecret: process.env.REACT_APP_STRIPE_WEBHOOK_SECRET || '',
  // Add your Stripe keys in environment variables for security
}; 